import { pipe } from "fp-ts/lib/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as Eq from "fp-ts/Eq";
import * as React from "react";
import { slugify } from "./utils";
import { throttle } from "./throttle";
import classNames from "classnames";

interface Props {
  articleContainerId: string;
}

/**
 * Iterates through all the elements in an article, assigning each
 * one a section ID. Allows keeping track of which section a user
 * is viewing based on the elements that are visible in the viewport.
 *
 * If the element encountered is an H2 update the current section ID.
 * For all other elements assign the ID to it using the `data-section`
 * attribute.
 *
 * @param article
 */
const assignChildSectionIds = (article: HTMLElement) => {
  Array.from(article.children).reduce((sectionId: string | null, element) => {
    if (element.tagName === "H2") {
      // the current H2 determines the current section we're in.
      return element.id;
    }
    if (sectionId) {
      // continue to assign the current section ID to all non-H2 elements
      element.setAttribute("data-section", sectionId);
    }
    return sectionId;
  }, null);
};

const isElementInViewport = (el: HTMLElement) => {
  var rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

const getSectionId = (el: HTMLElement) => {
  return O.fromNullable(
    el.tagName === "H2" ? el.id : el.getAttribute("data-section")
  );
};

/**
 * Dynamically generates a table of contents for an article by querying all
 * the `h2`s in the target element, generating a slug to assign to as an ID
 * and using the IDs to render a list of links to each heading.
 */
export const ArticleTableOfContents: React.FC<Props> = ({
  articleContainerId,
}) => {
  const [headings, setHeadings] = React.useState<
    Array<readonly [string, string]>
  >([]);

  const [currentSection, setCurrentSection] = React.useState("");

  React.useEffect(() => {
    // scrollspy behaviour
    const articleContainer = document.getElementById(articleContainerId);
    if (!articleContainer) {
      return;
    }

    const updateActiveSection = throttle(() => {
      pipe(
        Array.from(articleContainer.children),
        // find the first element that corresponds to a section that's visible
        A.findFirst(
          (el: HTMLElement) =>
            O.isSome(getSectionId(el)) && isElementInViewport(el)
        ),
        O.chain(getSectionId),
        O.map(setCurrentSection)
      );
    }, 10);

    updateActiveSection();

    window.addEventListener("scroll", updateActiveSection);

    return () => window.removeEventListener("scroll", updateActiveSection);
  }, [articleContainerId]);

  React.useEffect(() => {
    const articleContainer = document.getElementById(articleContainerId);

    if (!articleContainer) {
      return;
    }

    const headingElements = articleContainer.querySelectorAll("h2");

    // Extract the headings from the article by iterating over the
    // H2s and generating an ID for each headings
    const headings = pipe(
      Array.from(headingElements.entries()),
      A.filterMap(([, heading]) => {
        const headingTitle = heading.textContent;
        if (!headingTitle) {
          return O.none;
        }
        const slug = slugify(headingTitle);
        // set the ID of the h2 element to the slug of its content
        // so we can link to it
        heading.id = slug;
        return O.some([slug, headingTitle] as const);
      }),
      A.uniq(Eq.fromEquals(([slug1], [slug2]) => slug1 === slug2))
    );

    assignChildSectionIds(articleContainer);
    setHeadings(headings);
  }, [articleContainerId]);

  if (A.isEmpty(headings)) {
    return null;
  }

  return (
    <nav aria-label="Article contents">
      <ol className="space-y-1">
        {headings.map(([id, heading]) => (
          <li key={id}>
            <a
              href={`#${id}`}
              className={classNames(
                "block rounded-lg px-3 py-2 transition-colors hover:bg-neutral-100",
                currentSection === id ? "bg-neutral-100 font-medium" : ""
              )}
            >
              {heading}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};
